import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { FaLightbulb, FaUsers, FaLeaf, FaHeart, FaShieldAlt, FaHandshake, FaRocket, FaCalendarAlt, FaProjectDiagram, FaSmile, FaUserTie, FaGlobe, FaChevronLeft, FaChevronRight, FaStar, FaQuoteLeft } from 'react-icons/fa';
import Home from "../components/homePage/Home/Home";
import Divisions from "../components/homePage/Divisions/Divisions";
import Footer from "../components/Footer/Footer";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";

// Custom Navigation Bar for Home Page
function HomePageNavBar() {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-2xl shadow-xl border-b border-gray-200/30' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Brand Name - Home Page Version */}
          <div className="flex items-center">
            <a 
              href="/"
              className="group transition-all duration-300 hover:scale-105"
            >
              <span className={`text-2xl md:text-3xl font-bold font-comfortaa tracking-wide ${
                isScrolled ? 'text-green-900' : 'text-white'
              }`}>
                Arieri's <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500'>World Limited</span>
              </span>
            </a>
          </div>

          {/* Right Side Navigation */}
          <div className="flex items-center gap-6">
            {/* Home Page Section Links */}
            <div className="hidden lg:flex items-center gap-8">
              <a
                href="#home"
                className={`text-base font-medium hover:scale-105 transition-all duration-300 relative group ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Home
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600 group-hover:w-full transition-all duration-300`}></span>
              </a>
              <a
                href="#mission"
                className={`text-base font-medium hover:scale-105 transition-all duration-300 relative group ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                About
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600 group-hover:w-full transition-all duration-300`}></span>
              </a>
              <a
                href="/services"
                className={`text-base font-medium hover:scale-105 transition-all duration-300 relative group ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Services
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600 group-hover:w-full transition-all duration-300`}></span>
              </a>
              <a
                href="#divisions"
                className={`text-base font-medium hover:scale-105 transition-all duration-300 relative group ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Divisions
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600 group-hover:w-full transition-all duration-300`}></span>
              </a>
              <a
                href="/contact"
                className={`text-base font-medium hover:scale-105 transition-all duration-300 relative group ${
                  isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                Contact
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-600 group-hover:w-full transition-all duration-300`}></span>
              </a>
            </div>

            {/* Get Quote Button */}
            <a
              href="/request-quote"
              className={`px-4 py-2 rounded-xl font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${
                isScrolled
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md hover:shadow-green-500/25'
                  : 'bg-white/10 backdrop-blur-xl text-white hover:bg-white/20 border border-white/30 hover:border-white/50'
              }`}
            >
              Get Quote
            </a>

            {/* Mobile Menu Button */}
            <button className={`lg:hidden p-2 rounded-lg transition-all duration-300 ${
              isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>
  )
}

// Mission Statement Component
function MissionStatement() {
  return (
    <section className='py-20 bg-gradient-to-br from-green-50 via-white to-emerald-50 relative overflow-hidden'>
      {/* Background Pattern */}
      <div className='absolute inset-0'>
        {/* Subtle dots pattern */}
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-10' viewBox='0 0 100 100'>
            <defs>
              <pattern id='dots' x='0' y='0' width='10' height='10' patternUnits='userSpaceOnUse'>
                <circle cx='2' cy='2' r='0.5' fill='currentColor' className='text-green-400'/>
                <circle cx='7' cy='7' r='0.3' fill='currentColor' className='text-emerald-500'/>
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#dots)'/>
          </svg>
        </div>
        
        {/* Floating geometric shapes */}
        <div className='absolute top-20 right-1/4 w-16 h-16 border border-green-200/50 rounded-lg rotate-12 animate-pulse'></div>
        <div className='absolute bottom-32 left-1/3 w-12 h-12 border border-emerald-200/40 rounded-full animate-pulse animation-delay-300'></div>
        <div className='absolute top-1/2 right-1/3 w-8 h-8 bg-green-100/60 rounded-sm rotate-45 animate-pulse animation-delay-600'></div>
        
        {/* Subtle wave pattern */}
        <div className='absolute bottom-0 left-0 w-full h-32'>
          <svg className='w-full h-full opacity-20' viewBox='0 0 100 20' preserveAspectRatio='none'>
            <path d='M0,10 Q25,5 50,10 T100,10 L100,20 L0,20 Z' fill='currentColor' className='text-green-200'>
              <animate attributeName='d' dur='15s' repeatCount='indefinite' 
                values='M0,10 Q25,5 50,10 T100,10 L100,20 L0,20 Z;M0,10 Q25,15 50,10 T100,10 L100,20 L0,20 Z;M0,10 Q25,5 50,10 T100,10 L100,20 L0,20 Z'/>
            </path>
          </svg>
        </div>
      </div>
      
      <div className='container mx-auto px-6 relative z-10'>
        <div className='flex items-center gap-12 max-w-7xl mx-auto'>
          {/* Content */}
          <div className='flex-1'>
            <h2 className='text-4xl md:text-5xl font-bold text-gray-900 mb-8 font-comfortaa'>
              Our <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600'>Mission</span>
            </h2>
            <div className='w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-8'></div>
            <p className='text-xl text-gray-700 leading-relaxed font-light mb-6'>
              We exist to transform challenges into opportunities through the power of technology. Our mission is to serve society by creating 
              <span className='font-semibold text-green-600'> inclusive, sustainable, and practical innovations</span> that address real human needs.
            </p>
            <p className='text-lg text-gray-600 leading-relaxed'>
              Guided by humility and responsibility, we seek to give back at every step becoming a trusted solution provider that communities can rely on for growth and positive change.
            </p>
          </div>
          
          {/* Image */}
          <div className='flex-1 hidden lg:block'>
            <div className='relative'>
              <img 
                src='/assets/tech.jpg' 
                alt='Our Mission' 
                className='w-full h-96 object-cover rounded-2xl shadow-2xl'
              />
              <div className='absolute inset-0 bg-gradient-to-tr from-green-600/20 to-emerald-600/20 rounded-2xl'></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Vision Component
function Vision() {
  return (
    <section className='py-20 bg-gradient-to-r from-gray-900 via-green-900 to-emerald-900 relative overflow-hidden'>
      {/* Enhanced Background Pattern */}
      <div className='absolute inset-0'>
        {/* Geometric shapes */}
        <div className='absolute top-10 right-20 w-32 h-32 bg-gradient-to-br from-green-400/20 to-emerald-400/10 rounded-full animate-pulse'></div>
        <div className='absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-tl from-emerald-400/15 to-teal-400/5 rounded-full animate-pulse'></div>
        <div className='absolute top-1/2 left-1/3 w-16 h-16 bg-gradient-to-r from-green-300/10 to-emerald-300/20 rounded-full animate-pulse'></div>
        
        {/* Additional decorative elements */}
        <div className='absolute top-1/4 left-1/4 w-2 h-2 bg-green-400/30 rounded-full animate-bounce'></div>
        <div className='absolute top-3/4 right-1/4 w-3 h-3 bg-emerald-400/40 rounded-full animate-bounce animation-delay-300'></div>
        <div className='absolute bottom-1/4 right-1/3 w-1 h-1 bg-teal-400/50 rounded-full animate-ping'></div>
        
        {/* Grid pattern overlay */}
        <div className='absolute inset-0 bg-grid-pattern opacity-5'></div>
        
        {/* Flowing lines */}
        <div className='absolute top-0 left-0 w-full h-full'>
          <svg className='w-full h-full opacity-10' viewBox='0 0 100 100' preserveAspectRatio='none'>
            <path d='M0,20 Q25,10 50,20 T100,20' stroke='currentColor' strokeWidth='0.5' fill='none' className='text-green-400'>
              <animate attributeName='d' dur='8s' repeatCount='indefinite' 
                values='M0,20 Q25,10 50,20 T100,20;M0,30 Q25,20 50,30 T100,30;M0,20 Q25,10 50,20 T100,20'/>
            </path>
            <path d='M0,80 Q25,70 50,80 T100,80' stroke='currentColor' strokeWidth='0.5' fill='none' className='text-emerald-400'>
              <animate attributeName='d' dur='10s' repeatCount='indefinite' 
                values='M0,80 Q25,70 50,80 T100,80;M0,70 Q25,80 50,70 T100,70;M0,80 Q25,70 50,80 T100,80'/>
            </path>
          </svg>
        </div>
      </div>
      
      <div className='container mx-auto px-6 relative z-10'>
        <div className='flex items-center gap-12 max-w-7xl mx-auto'>
          {/* Image */}
          <div className='flex-1 hidden lg:block'>
            <div className='relative'>
              <img 
                src='/assets/vision.jpg' 
                alt='Our Vision' 
                className='w-full h-96 object-cover rounded-2xl shadow-2xl'
              />
              <div className='absolute inset-0 bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-2xl'></div>
            </div>
          </div>
          
          {/* Content */}
          <div className='flex-1'>
            <h2 className='text-4xl md:text-5xl font-bold text-white mb-8 font-comfortaa'>
              Our <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400'>Vision</span>
            </h2>
            <div className='w-24 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mb-8'></div>
            <p className='text-xl text-gray-200 leading-relaxed font-light'>
              We aspire to be a <span className='font-semibold text-green-400'>trusted companion to society</span> delivering diverse solutions 
              that touch lives, solve real problems, and bring happiness to communities.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// Core Values Component
function CoreValues() {
  const allValues = [
    {
      title: "Innovation",
      description: "We embrace technology to transform challenges into opportunities.",
      icon: FaLightbulb,
      number: "01"
    },
    {
      title: "Inclusivity",
      description: "We design solutions that serve everyone, leaving no one behind.",
      icon: FaUsers,
      number: "02"
    },
    {
      title: "Sustainability",
      description: "We prioritize long-term, responsible, and eco-conscious innovations.",
      icon: FaLeaf,
      number: "03"
    },
    {
      title: "Humility",
      description: "We serve with respect, empathy, and a people-first mindset.",
      icon: FaHeart,
      number: "04"
    },
    {
      title: "Responsibility",
      description: "We are accountable to society, our clients, and future generations.",
      icon: FaShieldAlt,
      number: "05"
    },
    {
      title: "Trustworthiness",
      description: "We build lasting relationships by being reliable and transparent.",
      icon: FaHandshake,
      number: "06"
    },
    {
      title: "Impact",
      description: "We focus on creating practical solutions that deliver real, measurable change.",
      icon: FaRocket,
      number: "07"
    }
  ];

  return (
    <section className='py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden'>
      {/* Background Pattern */}
      <div className='absolute inset-0'>
        {/* Circuit-like pattern */}
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-8' viewBox='0 0 200 200'>
            <defs>
              <pattern id='circuit' x='0' y='0' width='40' height='40' patternUnits='userSpaceOnUse'>
                <rect x='0' y='0' width='40' height='40' fill='none'/>
                <circle cx='10' cy='10' r='1' fill='currentColor' className='text-green-300'/>
                <circle cx='30' cy='30' r='1' fill='currentColor' className='text-emerald-300'/>
                <path d='M10,10 L30,10 L30,30' stroke='currentColor' strokeWidth='0.5' fill='none' className='text-green-300'/>
                <path d='M10,30 L30,30 L30,10' stroke='currentColor' strokeWidth='0.3' fill='none' className='text-emerald-300'/>
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#circuit)'/>
          </svg>
        </div>
        
        {/* Floating numbered elements */}
        <div className='absolute top-16 right-16 w-12 h-12 bg-green-100/60 rounded-full flex items-center justify-center text-green-600 font-bold text-sm animate-pulse'>01</div>
        <div className='absolute bottom-24 left-20 w-10 h-10 bg-emerald-100/50 rounded-full flex items-center justify-center text-emerald-600 font-bold text-xs animate-pulse animation-delay-400'>07</div>
        <div className='absolute top-1/3 right-1/4 w-8 h-8 bg-teal-100/40 rounded-full flex items-center justify-center text-teal-600 font-bold text-xs animate-pulse animation-delay-700'>03</div>
        
        {/* Connecting lines */}
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-15' viewBox='0 0 100 100'>
            <path d='M20,20 Q40,10 60,20 T100,30' stroke='currentColor' strokeWidth='1' fill='none' className='text-green-400' strokeDasharray='2,2'>
              <animate attributeName='stroke-dashoffset' dur='6s' repeatCount='indefinite' values='0;4;0'/>
            </path>
            <path d='M0,70 Q30,60 60,70 T100,80' stroke='currentColor' strokeWidth='1' fill='none' className='text-emerald-400' strokeDasharray='3,1'>
              <animate attributeName='stroke-dashoffset' dur='8s' repeatCount='indefinite' values='0;4;0'/>
            </path>
          </svg>
        </div>
      </div>
      
      <div className='container mx-auto px-6 relative z-10'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl font-bold text-gray-900 mb-8 font-comfortaa'>
              Our <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600'>Core Values</span>
            </h2>
            <div className='w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-600 mx-auto rounded-full mb-8'></div>
            <p className='text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto'>
              Seven fundamental principles that guide everything we do and shape our commitment to society.
            </p>
          </div>
          
          {/* All Values - Consistent Card Design */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
            {allValues.map((value, index) => (
              <div key={index} className='bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-green-100 hover:border-green-300 group'>
                <div className='flex items-start gap-6'>
                  <div className='flex-shrink-0'>
                    <div className='w-16 h-16 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300'>
                      <value.icon className='text-white text-xl' />
                    </div>
                    <div className='text-3xl font-bold text-green-200 font-comfortaa text-center'>{value.number}</div>
                  </div>
                  <div className='flex-1'>
                    <h3 className='text-2xl font-bold text-gray-900 mb-3 font-comfortaa'>{value.title}</h3>
                    <p className='text-gray-600 leading-relaxed'>{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// Call to Action Component
function CallToAction() {
  return (
    <section className='py-20 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 relative overflow-hidden'>
      <div className='absolute inset-0'>
        {/* Enhanced floating elements */}
        <div className='absolute top-10 right-20 w-32 h-32 bg-gradient-to-br from-yellow-300/20 to-white/10 rounded-full animate-pulse'></div>
        <div className='absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-tl from-white/15 to-yellow-200/10 rounded-full animate-pulse animation-delay-200'></div>
        <div className='absolute top-1/3 left-1/2 w-20 h-20 bg-gradient-to-r from-white/5 to-yellow-300/15 rounded-full animate-pulse animation-delay-400'></div>
        
        {/* Particle effects */}
        <div className='absolute top-1/4 right-1/3 w-3 h-3 bg-yellow-300/40 rounded-full animate-bounce'></div>
        <div className='absolute bottom-1/3 left-1/4 w-2 h-2 bg-white/50 rounded-full animate-bounce animation-delay-500'></div>
        <div className='absolute top-2/3 right-1/4 w-4 h-4 bg-yellow-200/30 rounded-full animate-bounce animation-delay-700'></div>
        
        {/* Hexagonal pattern */}
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-5' viewBox='0 0 60 60'>
            <defs>
              <pattern id='hexPattern' x='0' y='0' width='20' height='20' patternUnits='userSpaceOnUse'>
                <polygon points='10,2 18,7 18,13 10,18 2,13 2,7' fill='none' stroke='currentColor' strokeWidth='0.5' className='text-white'/>
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#hexPattern)'/>
          </svg>
        </div>
        
        {/* Radial gradient overlay */}
        <div className='absolute inset-0 bg-gradient-radial from-transparent via-green-500/5 to-emerald-700/10'></div>
      </div>
      
      <div className='container mx-auto px-6 text-center relative z-10'>
        <div className='max-w-4xl mx-auto'>
          <h2 className='text-4xl md:text-5xl font-bold text-white mb-6 font-comfortaa'>
            Ready to Transform Your <span className='text-yellow-300'>Future?</span>
          </h2>
          <p className='text-xl text-green-100 mb-10 leading-relaxed'>
            Join us in creating solutions that make a real difference. Let's build something extraordinary together.
          </p>
          
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <Link
              to='/services'
              className='bg-white text-green-600 hover:bg-yellow-300 hover:text-green-800 px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2'
            >
              Explore Our Services
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
              </svg>
            </Link>
            
            <Link
              to='/contact'
              className='border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center gap-2'
            >
              Get In Touch
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

// Animated Statistics Component
function AnimatedStatistics() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const statistics = [
    {
      icon: FaCalendarAlt,
      value: 1,
      suffix: '+',
      label: 'Years Experience',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: FaProjectDiagram,
      value: 6,
      suffix: '+',
      label: 'Projects Completed',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: FaSmile,
      value: 6,
      suffix: '+',
      label: 'Happy Clients',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: FaUserTie,
      value: 10,
      suffix: '+',
      label: 'Team Members',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: FaGlobe,
      value: 1,
      suffix: '+',
      label: 'Country Served',
      color: 'from-blue-500 to-indigo-500'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const CountUp = ({ end, suffix, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      setCount(0);
      let startTime;
      let animationId;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));

        if (progress < 1) {
          animationId = requestAnimationFrame(animate);
        }
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }, [end, duration, isVisible]);

    return <span>{count}{suffix}</span>;
  };

  return (
    <section ref={sectionRef} className='py-20 bg-gradient-to-r from-green-700 via-emerald-700 to-teal-700 relative overflow-hidden'>
      {/* Background Pattern */}
      <div className='absolute inset-0'>
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-10' viewBox='0 0 100 100'>
            <defs>
              <pattern id='statsPattern' x='0' y='0' width='20' height='20' patternUnits='userSpaceOnUse'>
                <rect x='8' y='8' width='4' height='4' fill='currentColor' className='text-white'/>
                <circle cx='5' cy='15' r='1' fill='currentColor' className='text-white'/>
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#statsPattern)'/>
          </svg>
        </div>
      </div>

      <div className='container mx-auto px-6 relative z-10'>
        <div className='text-center mb-16'>
          <h2 className='text-4xl md:text-5xl font-bold text-white mb-6 font-comfortaa'>
            Our <span className='text-yellow-300'>Achievements</span>
          </h2>
          <p className='text-xl text-green-100 max-w-3xl mx-auto'>
            Numbers that reflect our commitment to excellence and continuous growth in serving our clients.
          </p>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-6xl mx-auto'>
          {statistics.map((stat, index) => (
            <div key={index} className='text-center group'>
              <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${stat.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                <stat.icon className='text-white text-2xl' />
              </div>
              <div className='text-4xl md:text-5xl font-bold text-white mb-2 font-comfortaa'>
                <CountUp end={stat.value} suffix={stat.suffix} />
              </div>
              <p className='text-green-100 font-medium'>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Success Stories Carousel Component
function SuccessStoriesCarousel() {
  const [currentStory, setCurrentStory] = useState(0);

  const successStories = [
    {
      id: 1,
      title: "DirtBusters Digital Transformation",
      client: "DirtBusters Cleaning Services",
      industry: "Home Services & Cleaning",
      challenge: "Manual booking system and lack of online presence limiting customer reach and operational efficiency",
      solution: "Complete website development with online booking system, service scheduling, and customer management portal",
      results: [
        "200% increase in online bookings",
        "75% reduction in scheduling conflicts",
        "150% growth in customer base",
        "90% improvement in customer satisfaction"
      ],
      image: "/assets/projects/dirtbusters.png",
      testimonial: "Arieri's World completely transformed our business. The new website and booking system helped us reach more customers and streamline our operations. We've never been more organized!",
      clientName: "Mary Wanjiku",
      clientRole: "Founder, DirtBusters Cleaning Services"
    },
    {
      id: 2,
      title: "Erudite App Solutions Platform",
      client: "Erudite Solutions",
      industry: "Software Development & App Solutions",
      challenge: "Fragmented support systems and lack of centralized management platform for diverse app solutions portfolio",
      solution: "Comprehensive web application with centralized dashboard for support management, brand development, and multi-application oversight",
      results: [
        "Increased brand awareness significantly",
        "Enhanced online presence and visibility",
        "Centralized support management system",
        "Unified application management dashboard"
      ],
      image: "/assets/projects/erudite.png",
      testimonial: "Working with Arieri's World has been transformative for our business. They helped us increase our brand awareness, establish a strong online presence, and most importantly, centralized our support and application management from one comprehensive dashboard.",
      clientName: "Titus Muthomi",
      clientRole: "Founder, Erudite Solutions"
    },
    {
      id: 3,
      title: "Financial Analytics Dashboard",
      client: "InvestPro Analytics",
      industry: "Financial Services",
      challenge: "Complex data visualization needs and real-time market analysis requirements",
      solution: "Advanced analytics dashboard with AI-powered insights and real-time data processing",
      results: [
        "90% faster data processing",
        "200% improvement in decision speed",
        "150% increase in client portfolio performance"
      ],
      image: "/assets/christine.jpeg",
      testimonial: "The analytics platform gives us unprecedented insights into market trends and client needs.",
      clientName: "Amanda Rodriguez",
      clientRole: "CTO, InvestPro Analytics"
    }
  ];

  const nextStory = () => {
    setCurrentStory((prev) => (prev + 1) % successStories.length);
  };

  const prevStory = () => {
    setCurrentStory((prev) => (prev - 1 + successStories.length) % successStories.length);
  };

  const story = successStories[currentStory];

  return (
    <section className='py-12 bg-gradient-to-br from-gray-50 via-green-50 to-emerald-50 relative overflow-hidden'>
      {/* Background Pattern */}
      <div className='absolute inset-0'>
        <div className='absolute inset-0'>
          <svg className='w-full h-full opacity-5' viewBox='0 0 100 100'>
            <defs>
              <pattern id='successPattern' x='0' y='0' width='25' height='25' patternUnits='userSpaceOnUse'>
                <path d='M12.5,5 L20,12.5 L12.5,20 L5,12.5 Z' fill='currentColor' className='text-green-400'/>
              </pattern>
            </defs>
            <rect width='100%' height='100%' fill='url(#successPattern)'/>
          </svg>
        </div>
      </div>

      <div className='container mx-auto px-6 relative z-10'>
        <div className='text-center mb-10'>
          <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-comfortaa'>
            Success <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600'>Stories</span>
          </h2>
          <p className='text-lg text-gray-700 max-w-2xl mx-auto'>
            Real transformations, measurable results.
          </p>
        </div>

        <div className='max-w-5xl mx-auto'>
          <div className='bg-white rounded-2xl shadow-lg overflow-hidden border border-green-100'>
            <div className='grid lg:grid-cols-2 gap-0'>
              {/* Image Section */}
              <div className='relative h-64 lg:h-80'>
                <img 
                  src={story.image} 
                  alt={story.title}
                  className='w-full h-full object-cover'
                />
                <div className='absolute inset-0 bg-gradient-to-tr from-green-600/30 to-emerald-600/20'></div>
                
                {/* Industry Badge */}
                <div className='absolute top-4 left-4'>
                  <span className='bg-white/90 text-green-700 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm'>
                    {story.industry}
                  </span>
                </div>
              </div>

              {/* Content Section */}
              <div className='p-6 flex flex-col justify-center'>
                <h3 className='text-xl font-bold text-gray-900 mb-2 font-comfortaa'>{story.title}</h3>
                <p className='text-sm text-gray-600 mb-4'>{story.client}</p>
                
                <div className='mb-4'>
                  <h4 className='text-sm font-semibold text-gray-900 mb-1'>Results</h4>
                  <ul className='space-y-1'>
                    {story.results.slice(0, 2).map((result, index) => (
                      <li key={index} className='flex items-center text-gray-600 text-sm'>
                        <div className='w-1.5 h-1.5 bg-green-500 rounded-full mr-2'></div>
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Testimonial */}
                <div className='bg-green-50 rounded-xl p-4 mb-4'>
                  <FaQuoteLeft className='text-green-500 text-lg mb-2' />
                  <p className='text-gray-700 italic text-sm mb-2'>{story.testimonial}</p>
                  <div>
                    <p className='font-semibold text-gray-900 text-sm'>{story.clientName}</p>
                    <p className='text-xs text-gray-600'>{story.clientRole}</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className='flex items-center justify-between'>
                  <div className='flex space-x-1'>
                    {successStories.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentStory(index)}
                        className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                          index === currentStory ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  
                  <div className='flex space-x-1'>
                    <button 
                      onClick={prevStory}
                      className='w-8 h-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors duration-300 flex items-center justify-center text-sm'
                    >
                      <FaChevronLeft />
                    </button>
                    <button 
                      onClick={nextStory}
                      className='w-8 h-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors duration-300 flex items-center justify-center text-sm'
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className='overflow-hidden'>
      <HomePageNavBar />
      <div id="home">
        <Home />
      </div>
      <div id="mission">
        <MissionStatement />
        <Vision />
        <CoreValues />
        <AnimatedStatistics />
      </div>
      <div id="divisions">
        <Divisions />
      </div>
      {/* <SuccessStoriesCarousel /> */}     
      <div id="contact">
        <CallToAction />
      </div>
      <Footer />
      <ScrollToTop />
    </div>
  )
}
